// frontend/src/views/dashboard/FSC_Tabs.jsx
import React from "react";
import { Box, Tabs, Tab, Grid, TextField } from "@mui/material";
import SmartGauge from "../../components/gauges/SmartGauge";
import { FSC_GAUGES } from "../../config/gauges/fsc";
import { fscV3GaugeConfig } from "../../config/gauges/fsc_v3";

export default function FSC_Tabs({
  mode,
  data,
  loading,
  value,
  onChange,
  hideIdField,
}) {
  const isControlled = typeof value === "number" && typeof onChange === "function";
  const [internalTab, setInternalTab] = React.useState(0);

  const tab = isControlled ? value : internalTab;
  const setTab = (v) => (isControlled ? onChange(v) : setInternalTab(v));

  const isV3 = mode === "fsc_v3";

  // ✅ data segura (FSC normal)
  const maestro = data?.maestro ?? {};
  const modulos = Array.isArray(data?.modulos) ? data.modulos : [];

  const modulo = tab === 0 ? null : (modulos[tab - 1] ?? {});

  // ✅ data segura (FSC V3)
  // En V3 los gauges salen de latestRow (rows[0]) o latestRow/row
  const latestRow = data?.rows?.[0] ?? data?.latestRow ?? data?.row ?? {};

  // IDs
  // OJO: en tu dashboard para V3 el id real viene en numeroEquipo, NO en id
  const maestroId = isV3
    ? (latestRow?.numeroEquipo ?? latestRow?.id ?? "")
    : (maestro?.id ?? "");

  const moduloId = tab === 0
    ? ""
    : (isV3 ? (latestRow?.[`m${tab}NumeroEquipo`] ?? maestroId) : (modulo?.id ?? ""));

  // Tabs: 10 módulos fijos
  const moduleCount = 10;

  // ✅ Protecciones para evitar "Cannot read properties of undefined (reading 'length')"
  // (eso pasa cuando haces .map sobre undefined)
  const v3MaestroCfg = Array.isArray(fscV3GaugeConfig?.maestro)
    ? fscV3GaugeConfig.maestro
    : [];

  const v3ModuloRaw =
    typeof fscV3GaugeConfig?.modulo === "function" ? fscV3GaugeConfig.modulo(tab) : [];

  const v3ModuloCfg = Array.isArray(v3ModuloRaw) ? v3ModuloRaw : [];

  // 🔹 helper: convierte a número (por si viene string "12,3")
  const toNum = React.useCallback((v) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim().replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }, []);

  // ✅ normaliza ranges por si tu SmartGauge espera "bands" o "ranges"
  const normalizeRanges = (g) => {
    const bands = g?.bands ?? g?.ranges ?? [];
    return Array.isArray(bands) ? bands : [];
  };

  return (
    <Box>
      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1 }}
      >
        <Tab label="MAESTRO" value={0} />
        {Array.from({ length: moduleCount }, (_, i) => (
          <Tab key={i} label={`MÓDULO ${i + 1}`} value={i + 1} />
        ))}
      </Tabs>

      {/* ID Field */}
      {!hideIdField && (
        <Box sx={{ mb: 2 }}>
          <TextField
            label={tab === 0 ? "ID Maestro" : `ID Módulo ${tab}`}
            value={tab === 0 ? maestroId : (moduloId || maestroId)}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Box>
      )}

      {/* Gauges */}
      <Box>
        {/* =========================
            FSC V3 (usa tu config)
           ========================= */}
        {isV3 ? (
          tab === 0 ? (
            <Grid container spacing={2}>
              {v3MaestroCfg.map((g) => (
                <Grid key={g.key} item xs={12} md={6}>
                  <SmartGauge
                    title={g.title}
                    value={toNum(latestRow?.[g.key])}
                    unit={g.unit}
                    ranges={normalizeRanges(g)}
                    loading={loading}
                    min={g.min}
                    max={g.max}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {v3ModuloCfg.map((g) => (
                <Grid key={g.key} item xs={12} sm={6} md={3}>
                  <SmartGauge
                    title={g.title}
                    value={toNum(latestRow?.[g.key])}
                    unit={g.unit}
                    ranges={normalizeRanges(g)}
                    loading={loading}
                    min={g.min}
                    max={g.max}
                  />
                </Grid>
              ))}
            </Grid>
          )
        ) : (
          /* =========================
             FSC normal (tu lógica previa)
             ========================= */
          tab === 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SmartGauge
                  title={FSC_GAUGES?.maestro?.tProcesoEntrada?.title ?? "Temp. Proceso Entrada"}
                  value={toNum(maestro?.tProcesoEntrada)}
                  unit={FSC_GAUGES?.maestro?.tProcesoEntrada?.unit ?? ""}
                  ranges={FSC_GAUGES?.maestro?.tProcesoEntrada?.ranges ?? []}
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SmartGauge
                  title={FSC_GAUGES?.maestro?.tProcesoSalida?.title ?? "Temp. Proceso Salida"}
                  value={toNum(maestro?.tProcesoSalida)}
                  unit={FSC_GAUGES?.maestro?.tProcesoSalida?.unit ?? ""}
                  ranges={FSC_GAUGES?.maestro?.tProcesoSalida?.ranges ?? []}
                  loading={loading}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <SmartGauge
                  title={FSC_GAUGES?.modulo?.tEvapSal?.title ?? "Temp. Evap Salida"}
                  value={toNum(modulo?.tEvapSal)}
                  unit={FSC_GAUGES?.modulo?.tEvapSal?.unit ?? ""}
                  ranges={FSC_GAUGES?.modulo?.tEvapSal?.ranges ?? []}
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SmartGauge
                  title={FSC_GAUGES?.modulo?.tDescarga?.title ?? "Temp. Descarga"}
                  value={toNum(modulo?.tDescarga)}
                  unit={FSC_GAUGES?.modulo?.tDescarga?.unit ?? ""}
                  ranges={FSC_GAUGES?.modulo?.tDescarga?.ranges ?? []}
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SmartGauge
                  title={FSC_GAUGES?.modulo?.pSuccion?.title ?? "Pres. Succión"}
                  value={toNum(modulo?.pSuccion)}
                  unit={FSC_GAUGES?.modulo?.pSuccion?.unit ?? ""}
                  ranges={FSC_GAUGES?.modulo?.pSuccion?.ranges ?? []}
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SmartGauge
                  title={FSC_GAUGES?.modulo?.pDescarga?.title ?? "Pres. Descarga"}
                  value={toNum(modulo?.pDescarga)}
                  unit={FSC_GAUGES?.modulo?.pDescarga?.unit ?? ""}
                  ranges={FSC_GAUGES?.modulo?.pDescarga?.ranges ?? []}
                  loading={loading}
                />
              </Grid>
            </Grid>
          )
        )}
      </Box>
    </Box>
  );
}
